import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import ColorPicker from '@picsart/rc/old/ColorPicker';
import { getActiveEffects, updateEffectParams } from 'modules/effects';
import { useDispatch, useSelector } from 'react-redux';
import { getCurrentSceneId } from 'modules/scenes';
import { debounce, throttle } from 'throttle-debounce';
import { IRGB } from '@picsart/rc/types/containerComponents/ShapeSidebar/types';
import { getKonvaLayer } from 'modules/canvas';
import effectToKonvaTextSettings from 'utils/konva/effectToKonvaTextSettings';
import { saveRevision } from 'modules/revisions';
import { ToolName } from 'types/AppState';

type ColorPickerGeneral = {
  mode: 'outlineColor' | 'textColor' | 'shadowColor';
};

const TextColorPicker: FC<ColorPickerGeneral> = ({ mode }) => {
  const dispatch = useDispatch();
  const activeEffects = useSelector(getActiveEffects);
  const currentSceneId = useSelector(getCurrentSceneId);
  const layer = useSelector(getKonvaLayer);
  const [currentValue, setCurrentValue] = useState<IRGB>(
    activeEffects[0] ? activeEffects[0][mode] : null
  );

  const firstActiveEffectId = useMemo(() => {
    return activeEffects[0] ? activeEffects[0].id : null;
  }, [activeEffects]);

  useEffect(() => {
    setCurrentValue(activeEffects[0] ? activeEffects[0][mode] : null);
  }, [firstActiveEffectId]);

  const dispatcher = debounce(50, (dispatch, sceneId, effectIds, effectParams) => {
    effectIds.forEach((id: string) => {
      dispatch(updateEffectParams(sceneId, id, effectParams));
      dispatch(saveRevision(ToolName.Text));
    });
  });

  const debounceSetValue = useCallback(
    throttle(100, (rgb: IRGB) => setCurrentValue(rgb)),
    []
  );

  const onChange = useCallback(
    ({ rgb }) => {
      const newValue = rgb;
      const konvaObjects = activeEffects.map(effect => ({
        obj: layer.findOne(`#${effect.id}`),
        effect
      }));

      konvaObjects.forEach(obj => {
        const effectAttrs = effectToKonvaTextSettings({
          ...obj.effect,
          [mode]: newValue
        });
        obj.obj.setAttrs(effectAttrs);
      });

      layer.draw();

      debounceSetValue(newValue);

      dispatcher(
        dispatch,
        currentSceneId,
        activeEffects.map(effect => effect.id),
        {
          [mode]: newValue
        }
      );
    },
    [mode, activeEffects, layer, dispatch]
  );

  return activeEffects.length ? (
    <ColorPicker
      context={null}
      format={'rgba' as any}
      onChange={onChange}
      values={[currentValue]}
      customPresetsColors={[]}
    />
  ) : null;
};

export default TextColorPicker;
